import Phaser from 'phaser';
import { PuppyGame, maxPuppiesForLevel, maxColorsForLevel } from '../game/PuppyGame';
import { Board } from '../game/Board';
import { defaultRng } from '../game/rng';
import { allPuppies, puppyForId, puppyForIndex } from '../game/Puppy';
import type { PuppyId, Color } from '../game/types';
import { MegaPuppy } from '../game/MegaPuppy';
import { BoardView } from '../render/BoardView';
import { HeaderView } from '../render/HeaderView';
import { FooterView } from '../render/FooterView';
import { eruptScoreAt } from '../render/ScoreEruption';
import { getPlayableSquares, suggestBreedArmCell } from '../game/Hints';
import { shouldTriggerWeather } from '../game/Weather';
import { playThunderstorm } from '../render/Thunderstorm';
import { FoodBonus } from '../render/FoodBonus';
import { SettingsOverlay, type SettingsState } from '../render/SettingsOverlay';
import { SaveStore } from '../persistence/SaveStore';
import { SoundBank } from '../audio/SoundBank';
import { LOGICAL_WIDTH, LOGICAL_HEIGHT } from '../render/layout';
import { registerPuppyAnimations } from '../render/puppyAnimations';
import { atlasKeysForPuppies } from '../render/puppyAtlases';

// Mirrors iOS squaresbevel atlas color0..color6.
const COLORS: Color[] = ['red', 'yellow', 'orange', 'blue', 'green', 'pink', 'purple'];

// Mirrors GameScene.swift::maxDogFoodBonus (line 722). Index = (levelNumber - 1).
// Levels past the table fall back to the last entry.
const MAX_FOOD_BONUS_PER_LEVEL: readonly number[] = [
  2, 2, 2, 2, 1, 1, 1, 1, 1, 1,
  1, 1, 0, 1, 0, 0, 1, 1, 0, 0,
  0, 1, 0, 0, 0, 0, 1, 0, 0, 0,
  0, 0, 1, 1, 0, 0, 0, 1, 0, 0,
  0, 0, 0, 1, 0, 0, 0, 0, 0, 0,
  1, 1, 1, 0, 0, 0, 0, 0, 0, 1,
  1, 0, 0, 0, 0, 0, 0, 1, 0, 0,
  0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
  0, 0, 0, 0, 1, 0, 0, 0, 0, 1,
  0, 0, 0, 0, 0, 0, 1, 0, 0, 0,
  0,
];
function maxFoodBonusForLevel(level: number): number {
  const idx = Math.max(0, Math.abs(level) - 1);
  return MAX_FOOD_BONUS_PER_LEVEL[Math.min(idx, MAX_FOOD_BONUS_PER_LEVEL.length - 1)]!;
}

interface MainSceneInitData {
  advanceLevel?: boolean;
}

export class MainScene extends Phaser.Scene {
  private puppyGame!: PuppyGame;
  private boardView!: BoardView;
  private headerView!: HeaderView;
  private footerView!: FooterView;
  private save = new SaveStore();
  // Resolved at runtime from Vite's base URL so dev and prod both work.
  private soundBank = new SoundBank(
    `${(import.meta as unknown as { env?: { BASE_URL?: string } }).env?.BASE_URL ?? '/'}assets/audio`,
  );
  private active: { puppy: PuppyId; color: Color } = { puppy: 'doug', color: 'red' };
  private advanceOnCreate = false;
  // Set true for one tick when a SuperMega is tapped. A SuperMega tap fires
  // the mega sprite's gameobjectdown (-> onMegaTapped) AND, immediately after,
  // the scene-level board pointerdown (-> tryPlace) for the same screen point.
  // Without this guard the tap both converts the SuperMega and drops the
  // active puppy into one of the just-cleared cells. Cleared on the next tick.
  private megaJustTapped = false;
  // True between create() and the end of the first-launch Welcome/Dog-Training
  // overlay. While set, the onboarding hint is held back so its paw doesn't
  // animate and fade unseen behind that overlay; openDogTraining re-fires it.
  private pendingOnboardingHint = false;
  private settingsOverlay: SettingsOverlay | null = null;
  private settings: SettingsState = { music: true, sfx: true, hints: true, kidMode: false };
  private foodBonus!: FoodBonus;
  // Snapshot of the most recent placement so the user can undo it. Cleared
  // on next placement, level change, or once the undo button times out.
  private lastUndo: {
    cellIndex: number;
    scoreDelta: number;
    prevActive: { puppy: PuppyId; color: Color };
  } | null = null;
  private undoHideTimer: Phaser.Time.TimerEvent | null = null;
  // Guards startBackgroundPrefetch so the idle prefetch only kicks off once.
  private prefetchStarted = false;
  // The in-scene "loading new puppies…" indicator, shown only when a level
  // introduces puppies whose atlases aren't cached yet.
  private loadingText: Phaser.GameObjects.Text | null = null;
  private loadingBarFrame: Phaser.GameObjects.Graphics | null = null;
  private loadingBarFill: Phaser.GameObjects.Graphics | null = null;

  constructor() {
    super({ key: 'Main' });
  }

  init(data?: MainSceneInitData): void {
    this.advanceOnCreate = !!data?.advanceLevel;
  }

  create(): void {
    // Camera bg shows through gaps (e.g., the small vertical padding above
    // the cell grid in the BoardView region). Use grass green so the gap
    // blends with surrounding tiles. iOS uses a lighter scene bg, but its
    // header/footer art covers the equivalent area edge-to-edge.
    this.cameras.main.setBackgroundColor('#5fa84a');

    if (this.advanceOnCreate && this.puppyGame) {
      this.puppyGame.levelNumber += 1;
      this.puppyGame.levelScore = 0;
      this.puppyGame.board = new Board(this.puppyGame.numSquaresHoriz, this.puppyGame.numSquaresVert);
      this.seedFireHydrant();
      this.puppyGame.board.placeObstaclesForLevel(
        this.puppyGame.levelNumber,
        defaultRng(this.puppyGame.levelNumber),
      );
      this.rerollLevelRoster();
      // Clear the previous level's active so the restore branch below picks
      // a fresh puppy from the NEW level's roster. Otherwise the player gets
      // exactly one (puppy, color) that isn't in puppiesThisLevel /
      // colorsThisLevel — making it impossible to chain or replace.
      this.puppyGame.currPuppy = 0;
      this.puppyGame.currColor = 0;
    } else {
      const bundle = this.save.load();
      this.puppyGame = new PuppyGame({ rng: defaultRng(Date.now()) });
      this.puppyGame.applyBundle(bundle);
      // First launch (no saved game state) → seed obstacles + hydrant.
      // Returning player → applyBundle already restored everything.
      if (bundle.state === null) {
        this.seedFireHydrant();
        this.puppyGame.board.placeObstaclesForLevel(
          this.puppyGame.levelNumber,
          defaultRng(this.puppyGame.levelNumber),
        );
        this.rerollLevelRoster();
      } else if (
        this.puppyGame.puppiesThisLevel.length === 0 ||
        this.puppyGame.colorsThisLevel.length === 0
      ) {
        // Old saves before per-level roster persistence — re-roll once so
        // we don't fall back to all 10 puppies / 7 colours.
        this.rerollLevelRoster();
      }
    }

    // Gate the visible build on this level's puppy atlases. Fast path (already
    // cached — returning player or prefetched) builds synchronously; otherwise
    // load just this roster's atlases, then build.
    const needed = atlasKeysForPuppies(this.puppyGame.puppiesThisLevel);
    const missing = needed.filter((key) => !this.textures.exists(key));
    if (missing.length === 0) {
      this.buildScene();
      return;
    }
    this.showLoadingIndicator();
    const base = (import.meta as unknown as { env?: { BASE_URL?: string } }).env?.BASE_URL ?? '/';
    this.load.setPath(`${base}assets`);
    for (const key of missing) {
      this.load.atlas(key, `atlases/${key}.png`, `atlases/${key}.json`);
    }
    this.load.once('complete', () => {
      registerPuppyAnimations(this);
      this.hideLoadingIndicator();
      this.buildScene();
    });
    this.load.start();
  }

  // Builds the visible scene for the current level. Requires this level's
  // puppy atlases to be loaded (gated in create()).
  private buildScene(): void {
    const parkData = this.cache.json.get('parknames') as { 'Park Names'?: string[] } | undefined;
    const parkNames = parkData?.['Park Names'] ?? [];
    this.puppyGame.parkNames = parkNames;

    this.boardView = new BoardView(this, this.puppyGame.board, (cell) => {
      if (this.settingsOverlay) return;
      // A SuperMega tap already consumed this click (conversion). Don't also
      // place the active puppy into the cell it cleared.
      if (this.megaJustTapped) return;
      if (this.scene.isActive('DogTraining') || this.scene.isActive('Welcome')) return;
      this.tryPlace(cell);
    });
    this.headerView = new HeaderView(this, this.puppyGame, () => this.soundBank.playSfx('dogbowl'));
    this.footerView = new FooterView(this, this.puppyGame, parkNames, () => this.openDogTraining());
    this.foodBonus = new FoodBonus(this, (idx) => this.boardView.cellCenter(idx), this.boardView.cellWidth);

    this.headerView.crazyTile.on('pointerdown', () => this.useCrazySquare());
    this.headerView.pawMenuButton.on('pointerdown', () => this.openSettings());
    this.headerView.dogHouse.on('pointerdown', () => this.goToDogHouse());
    this.headerView.undoButton.on('pointerdown', () => this.doUndo());

    // Debug: press X to force the Oh-No flow. Useful for testing the replay path.
    // this.input.keyboard?.on('keydown-X', () => this.openOhNo());
    // Debug: press A to open the per-puppy animation viewer. ESC returns.
    this.input.keyboard?.on('keydown-A', () => this.openAnimDebug());
    // Debug: press R to wipe the save bundle and restart the scene (same as
    // Settings → New Game → OK).
    this.input.keyboard?.on('keydown-R', () => this.startNewGame());

    // Hydrate settings from saved bundle. Fall back to current defaults if
    // the bundle is from a build before settings were persisted.
    const loaded = this.save.load();
    if (loaded.settings) {
      this.settings = { ...loaded.settings };
    }

    // On a fresh game the Welcome → Dog-Training overlay launches below and
    // covers the board, so the onboarding paw fired by setActive would animate
    // and fade unseen. Defer it; openDogTraining re-shows it once the player is
    // back on an interactive board. (Computed before setActive, which is what
    // fires runTurnStartHint, and before line ~217 flips hasStartedGame.)
    this.pendingOnboardingHint =
      !this.puppyGame.hasFinishedDogTraining && !this.puppyGame.hasStartedGame;

    // Restore the saved Big Puppy if there is one; otherwise pick a fresh
    // one. puppyGame.currPuppy is the 1-indexed puppyNum (0 = unset).
    if (this.puppyGame.currPuppy > 0) {
      const puppyId = puppyForIndex(this.puppyGame.currPuppy - 1).id;
      const color = COLORS[this.puppyGame.currColor] ?? 'red';
      // Also require the saved (puppy, color) to be in this level's roster —
      // otherwise the player would see a one-off combo that no future
      // placement can chain to. Belt-and-suspenders: even with currPuppy
      // cleared on level advance, restoring from an older save bundle
      // could still hand back an out-of-roster pair.
      const inRoster =
        this.puppyGame.puppiesThisLevel.includes(puppyId) &&
        this.puppyGame.colorsThisLevel.includes(color);
      const { playableSquares } = getPlayableSquares(
        this.puppyGame.board, puppyId, color, false, false,
      );
      if (inRoster && playableSquares > 0) {
        this.setActive({ puppy: puppyId, color });
      } else {
        this.setActive(this.pickActivePuppy());
      }
    } else {
      this.setActive(this.pickActivePuppy());
    }
    this.boardView.rerender();
    // Restore visuals for any MegaPuppies persisted in the save bundle.
    // formMegaPuppy(animate=false) builds them at rest scale with no entrance
    // flourishes, matching the state the player left them in.
    for (const mp of this.puppyGame.board.megaPuppies) {
      const puppyId = puppyForIndex(mp.puppy - 1).id;
      this.boardView.formMegaPuppy(
        mp.index,
        puppyId,
        mp.isSuperMega,
        () => this.onMegaTapped(mp.index),
        false,
      );
    }
    this.headerView.refresh(this.active.puppy, this.active.color);
    this.footerView.refresh();
    this.updateHint();
    this.maybeShowGoToDogHouse();
    // If the restored state is unrecoverable (no playable squares + doghouse
    // full + no crazysquares banked), surface the Oh-No flow on load instead
    // of leaving the player visibly stuck.
    if (this.isStuck()) this.openOhNo();
    // 15-px debug grid overlay (uncomment to re-enable).
    // this.drawDebugGrid();

    if (!this.puppyGame.hasFinishedDogTraining && !this.puppyGame.hasStartedGame) {
      this.puppyGame.hasStartedGame = true;
      this.openWelcome();
    }

    // Kick off background music. The BootScene tap-to-start already unlocked
    // browser audio, so this is safe.
    this.soundBank.setMusicEnabled(this.settings.music);
    this.soundBank.setSfxEnabled(this.settings.sfx);
    if (this.settings.music) {
      const tracks: ('bkgndmusic1' | 'bkgndmusic2' | 'bkgndmusic3' | 'bkgndmusic4')[] = [
        'bkgndmusic1', 'bkgndmusic2', 'bkgndmusic3', 'bkgndmusic4',
      ];
      this.soundBank.startMusic(tracks[this.puppyGame.randomInt(tracks.length)]!);
    }

    const flush = (): void => this.save.flushSave();
    window.addEventListener('visibilitychange', flush);
    window.addEventListener('pagehide', flush);
    this.events.on('shutdown', () => {
      window.removeEventListener('visibilitychange', flush);
      window.removeEventListener('pagehide', flush);
    });

    // Warm the rest of the assets now that the first board is interactive.
    this.startBackgroundPrefetch();
  }

  private showLoadingIndicator(): void {
    this.cameras.main.setBackgroundColor('#5fa84a');
    this.loadingText = this.add
      .text(LOGICAL_WIDTH / 2, LOGICAL_HEIGHT / 2 - 50, 'Fetching puppies…', {
        fontFamily: 'SnackerComic, system-ui, sans-serif',
        fontSize: '64px',
        color: '#ffffff',
      })
      .setOrigin(0.5);

    // Progress bar (mirrors PreloadScene) so this phase shows movement instead
    // of looking hung — it loads the level's puppy atlases, the heaviest assets.
    const barW = LOGICAL_WIDTH * 0.6;
    const barH = 18;
    const barX = (LOGICAL_WIDTH - barW) / 2;
    const barY = LOGICAL_HEIGHT / 2 + 30;

    this.loadingBarFrame = this.add.graphics();
    this.loadingBarFrame.lineStyle(3, 0xffffff, 0.9).strokeRoundedRect(barX, barY, barW, barH, 9);

    this.loadingBarFill = this.add.graphics();
    this.load.on('progress', (value: number) => {
      this.loadingBarFill?.clear();
      this.loadingBarFill
        ?.fillStyle(0xffffff, 0.95)
        .fillRoundedRect(barX + 2, barY + 2, (barW - 4) * value, barH - 4, 7);
    });
  }

  private hideLoadingIndicator(): void {
    // Drop our progress listener so the later background prefetch (same loader)
    // doesn't try to draw into destroyed graphics.
    this.load.off('progress');
    this.loadingText?.destroy();
    this.loadingText = null;
    this.loadingBarFill?.destroy();
    this.loadingBarFill = null;
    this.loadingBarFrame?.destroy();
    this.loadingBarFrame = null;
  }

  // After the first board is up, stream in every puppy atlas not yet loaded
  // so later levels are warm. (Videos are in the critical set, not here.)
  // Best-effort: if a level advance triggers an on-demand load first, that
  // path covers correctness. registerPuppyAnimations is idempotent.
  private startBackgroundPrefetch(): void {
    if (this.prefetchStarted) return;
    this.prefetchStarted = true;

    const base = (import.meta as unknown as { env?: { BASE_URL?: string } }).env?.BASE_URL ?? '/';
    this.load.setPath(`${base}assets`);

    let queued = 0;
    for (const p of allPuppies()) {
      for (let a = 1; a <= p.numAnims; a++) {
        const key = `p${p.puppyNum}a${a}`;
        if (this.textures.exists(key)) continue;
        this.load.atlas(key, `atlases/${key}.png`, `atlases/${key}.json`);
        queued++;
      }
    }

    if (queued === 0) return;
    this.load.once('complete', () => registerPuppyAnimations(this));
    this.load.start();
  }

  // Mirrors PuppyGame.swift's defaultHydrantIndex = numSquaresHoriz * 4 + 3.
  // The hydrant gives the first puppy placement something to score against;
  // without it, calcPlaceScore returns aggregateScore == 0 → false everywhere.
  private seedFireHydrant(): void {
    const idx = this.puppyGame.numSquaresHoriz * 4 + 3;
    if (idx >= this.puppyGame.board.numSquares) return;
    const sq = this.puppyGame.board.squareAt(idx);
    if (this.puppyGame.board.obstacleAtIndex(idx)) return;
    sq.data.isFireHydrant = true;
    sq.data.isClear = true;
  }

  // Updates the active Big Puppy and mirrors the choice into puppyGame's
  // currPuppy/currColor fields so persist() writes them to the save bundle.
  // currPuppy is 1-indexed (puppyNum); currColor is an index into COLORS.
  private setActive(next: { puppy: PuppyId; color: Color }): void {
    this.active = next;
    this.puppyGame.currPuppy = puppyForId(next.puppy).puppyNum;
    this.puppyGame.currColor = COLORS.indexOf(next.color);
    this.runTurnStartHint();
  }

  // Pulse the paw at the best playable cell as a first-turns onboarding
  // hint — Hints on, Kid Mode OFF, and hintsPlayDog still under 5. In Kid
  // Mode the always-on paw covers this already, so the onboarding pulse is
  // suppressed there. hintsPlayDog counts up each fire and persists.
  private runTurnStartHint(): void {
    // Held back while the first-launch Welcome/Dog-Training overlay is up — the
    // paw would otherwise animate and fade unseen behind it. openDogTraining
    // clears the flag and re-fires this once the board is interactive again.
    if (this.pendingOnboardingHint) return;
    if (!this.settings.hints) return;
    if (this.settings.kidMode) return;
    if (this.puppyGame.hintsPlayDog >= 5) {
      // Onboarding done (5th puppy placed) and not in Kid Mode — clear the paw
      // now instead of letting the last pulse finish its fade.
      this.boardView.hideOnboardingPaw();
      return;
    }
    const cell = this.bestHintCell();
    if (cell < 0) return;
    this.boardView.animateHintPaw(cell, 2);
    this.puppyGame.hintsPlayDog += 1;
  }

  // Cell the hint paw should point at: the next cell on the active breed's arm
  // radiating from the fire hydrant. Falls back to the highest-scoring playable
  // cell when the arm logic yields nothing (no hydrant / arm blocked). Returns
  // -1 when the active puppy has no playable square at all (caller hides hint).
  // Note: getPlayableSquares runs first for its scoreIfPlayable side effect,
  // which the fallback below reads.
  private bestHintCell(): number {
    const { playableSquares, maxScore } = getPlayableSquares(
      this.puppyGame.board,
      this.active.puppy,
      this.active.color,
      false,
      false,
    );
    if (playableSquares === 0 || maxScore <= 0) return -1;
    const arm = suggestBreedArmCell(
      this.puppyGame.board,
      this.active.puppy,
      this.active.color,
    );
    if (arm >= 0) return arm;
    // Fallback: first max-score cell, preferring a not-yet-clear one.
    let preferred = -1;
    let fallback = -1;
    for (let i = 0; i < this.puppyGame.board.numSquares; i++) {
      const sq = this.puppyGame.board.squareAt(i).data;
      if (sq.scoreIfPlayable !== maxScore) continue;
      if (fallback < 0) fallback = i;
      if (!sq.isClear) {
        preferred = i;
        break;
      }
    }
    return preferred >= 0 ? preferred : fallback;
  }

  private pickActivePuppy(): { puppy: PuppyId; color: Color } {
    // The per-level roster MUST be chosen before the first Big Puppy is set.
    // If it's still empty here, reroll it now rather than falling back to the
    // full 11-breed / 7-colour pool — an out-of-roster puppy placed on the
    // board can never recur, permanently blocking the level (its empty
    // neighbours stay unplayable, so isLevelClear never becomes true).
    if (
      this.puppyGame.puppiesThisLevel.length === 0 ||
      this.puppyGame.colorsThisLevel.length === 0
    ) {
      this.rerollLevelRoster();
    }
    // Pull only from the per-level breed/colour subsets so each level has a
    // smaller, more focused mix (matches iOS).
    const breeds = this.puppyGame.puppiesThisLevel;
    const colors = this.puppyGame.colorsThisLevel;
    const drawOne = (): { puppy: PuppyId; color: Color } => ({
      puppy: breeds[this.puppyGame.randomInt(breeds.length)]!,
      color: colors[this.puppyGame.randomInt(colors.length)]!,
    });
    // Prefer a combo that has at least one playable cell on the current
    // board. Up to 24 retries — well under the worst-case roster size
    // (5 puppies × 6 colours = 30 combos). If every combo is unplayable,
    // accept the last draw; the player will fall back to the doghouse /
    // crazysquare / game-over path.
    let last = drawOne();
    for (let i = 0; i < 24; i++) {
      const { playableSquares } = getPlayableSquares(
        this.puppyGame.board, last.puppy, last.color, false, false,
      );
      if (playableSquares > 0) return last;
      last = drawOne();
    }
    return last;
  }

  // Picks a fresh random subset of breeds and colours for the current
  // level. Called when starting a fresh game or advancing to a new level.
  private rerollLevelRoster(): void {
    const maxP = maxPuppiesForLevel(this.puppyGame.levelNumber);
    const maxC = maxColorsForLevel(this.puppyGame.levelNumber);
    const roster = allPuppies().map((p) => p.id);
    this.puppyGame.maxPuppiesThisLevel = maxP;
    this.puppyGame.maxColorsThisLevel = maxC;
    this.puppyGame.puppiesThisLevel = this.sampleWithoutReplacement(roster, maxP);
    this.puppyGame.colorsThisLevel = this.sampleWithoutReplacement([...COLORS], maxC);
  }

  private sampleWithoutReplacement<T>(pool: T[], n: number): T[] {
    const copy = [...pool];
    const out: T[] = [];
    const take = Math.min(n, copy.length);
    for (let i = 0; i < take; i++) {
      const idx = this.puppyGame.randomInt(copy.length);
      out.push(copy.splice(idx, 1)[0]!);
    }
    return out;
  }

  private indexOfPuppy(id: PuppyId): number {
    return puppyForId(id).puppyNum;
  }

  private tryPlace(cellIndex: number): void {
    // Capture the active puppy/color before placement so undo can restore it.
    const prevActive = { puppy: this.active.puppy, color: this.active.color };

    const r = this.puppyGame.board.applyPlacement(
      cellIndex, this.active.puppy, this.active.color, false, false,
    );
    if (!r.placed) {
      this.soundBank.playSfx('whimper');
      return;
    }
    // Base placement sound — mirrors iOS Square.swift:783 (clickSound). The
    // followup "good job" sound is queued below based on what the placement
    // did (high score / row-col clear / mega formation).
    this.soundBank.playSfx('click');
    const clearedSomething0 = r.clearedRow || r.clearedCol;

    // +50 adjacent-to-MegaPuppy bonus — mirrors iOS Square.swift:787-806.
    // Only counts on plain placements (no row/col clear, no new mega).
    // First adjacent mega wins; +50 is added to the score, and a random
    // wow OR yeah is queued.
    let plus50 = false;
    if (!r.madeMega && !clearedSomething0) {
      const horiz = this.puppyGame.numSquaresHoriz;
      for (const mp of this.puppyGame.board.megaPuppies) {
        if (mp.indexAdjacentToMegaPuppy(cellIndex, horiz)) {
          plus50 = true;
          break;
        }
      }
    }
    if (plus50) {
      this.time.delayedCall(250, () => {
        this.soundBank.playSfx(Math.random() < 0.5 ? 'wow' : 'yeah');
      });
    }

    if (r.madeMega) {
      // iOS Square.swift:826-827 — megapup at 0.68s, then a random "good
      // job" 1.5s after that. (megapup itself is fired at line 381 below.)
      this.time.delayedCall(680 + 1500, () => this.soundBank.playRandomGoodJob());
    } else if (r.score >= 75) {
      // iOS Board.swift:2074 — playRandomGoodJob after 0.75s. Uses the
      // base placement score; the +50 adjacency bonus is added below.
      this.time.delayedCall(750, () => this.soundBank.playRandomGoodJob());
    } else if (clearedSomething0) {
      // iOS Board.swift:2076 — playRandomGoodJob after 0.94s.
      this.time.delayedCall(940, () => this.soundBank.playRandomGoodJob());
    }
    if (plus50) {
      // Apply the +50 to score AFTER the threshold check above so the
      // bonus doesn't artificially push a placement past the 75 gate
      // (matches iOS, which evaluates addToScore pre-doPlus50).
      r.score += 50;
    }

    this.puppyGame.gameScore += r.score;
    this.puppyGame.levelScore += r.score;
    this.puppyGame.gamePuppiesPlayed += 1;
    this.puppyGame.levelPuppiesPlayed += 1;

    // Bonus claim — mirrors iOS Board.swift:2027. A successful placement on
    // the bonus square awards a CrazySquare and triggers the fly-away tween.
    if (this.puppyGame.board.bonusSquare === cellIndex) {
      this.puppyGame.board.clearBonus();
      this.puppyGame.crazySquares += 1;
      this.puppyGame.gameBonuses += 1;
      this.soundBank.playSfx('yeah');
      this.foodBonus.collect(() => {
        this.headerView.refresh(this.active.puppy, this.active.color);
        this.boardView.rerender();
        this.persist();
      });
    }

    // Undo snapshot for plain placements only, and only on the first three
    // turns of the game (training-wheels window — after that, no undo). A
    // MegaPuppy formation OR a row/col clear touches too much state — skip
    // undo there too.
    const clearedSomething = r.clearedRow || r.clearedCol;
    if (!r.madeMega && !clearedSomething && this.puppyGame.gamePuppiesPlayed <= 3) {
      this.recordUndo({ cellIndex, scoreDelta: r.score, prevActive });
    } else {
      this.clearUndo();
    }

    const center = this.boardView.cellCenter(cellIndex);
    // Tear down visuals for any MegaPuppies removed by the row/col clear
    // before rerender so the next frame doesn't show stale sprites on top
    // of now-empty cells.
    for (const tl of r.removedMegaTopLefts) {
      this.boardView.destroyMegaVisual(tl);
    }
    this.boardView.rerender();
    // Only show the placement bounce when the placed puppy survives — a
    // row/col clear wipes it before any animation could complete.
    if (!clearedSomething) {
      this.boardView.animatePlacement(cellIndex);
    } else {
      // Sparkle each cleared cell so the row/col wipe has visual punch.
      for (const idx of r.clearedIndexes) {
        this.boardView.sparkleAt(idx);
      }
    }
    eruptScoreAt(this, center.x, center.y, r.score);

    if (r.madeMega) {
      const mp = new MegaPuppy();
      mp.attachAt(
        r.megaTopLeftIndex,
        this.indexOfPuppy(this.active.puppy),
        r.madeSuperMega,
        this.puppyGame.numSquaresHoriz,
      );
      this.puppyGame.board.megaPuppies.push(mp);
      this.boardView.formMegaPuppy(r.megaTopLeftIndex, this.active.puppy, r.madeSuperMega, () =>
        this.onMegaTapped(r.megaTopLeftIndex),
      );

      // Diagnostic: snapshot the 4 underlying cells right after formation.
      {
        const horiz = this.puppyGame.numSquaresHoriz;
        const cells = [
          r.megaTopLeftIndex,
          r.megaTopLeftIndex + 1,
          r.megaTopLeftIndex + horiz,
          r.megaTopLeftIndex + 1 + horiz,
        ];
        const snap = cells.map((i) => {
          const d = this.puppyGame.board.squareAt(i).data;
          return { i, puppy: d.puppy, color: d.color, isClear: d.isClear, underMega: d.underCrazyMega };
        });
        console.log(
          `[mega-form] tl=${r.megaTopLeftIndex} super=${r.madeSuperMega} active=${this.active.puppy}/${this.active.color}\n  cells: ${JSON.stringify(snap)}`,
        );
      }
      // Mirrors Board.swift:2067-2070 — every mega formation (super or
      // plain) bumps the level counter and may raise the peak. The game
      // counter also bumps; super megas additionally bump the game-level
      // "super mega" tally that was previously the only thing tracked.
      this.puppyGame.levelMegaPuppies += 1;
      this.puppyGame.gameMegaPuppies += 1;
      const peak = this.puppyGame.board.megaPuppies.length;
      if (peak > this.puppyGame.levelMaxMegaPuppies) this.puppyGame.levelMaxMegaPuppies = peak;
      if (peak > this.puppyGame.gameMaxMegaPuppies) this.puppyGame.gameMaxMegaPuppies = peak;
      this.soundBank.playSfx('megapup');
    }

    if (this.puppyGame.board.isLevelClear()) {
      this.soundBank.playSfx('levelsuccess');
      const g = this.puppyGame;
      // Roll the per-level totals into the lifetime "Most"/"Best" stats so the
      // Interstitial displays max(prior, current) — the all-time records
      // across every level played, including the one just finished.
      if (g.levelScore > g.bestLevelScore) g.bestLevelScore = g.levelScore;
      if (g.levelPuppiesPlayed > g.mostPuppiesPlayed) g.mostPuppiesPlayed = g.levelPuppiesPlayed;
      if (g.levelMegaPuppies > g.mostMegaPuppies) g.mostMegaPuppies = g.levelMegaPuppies;
      if (g.levelMaxMegaPuppies > g.mostMaxMegaPuppies) g.mostMaxMegaPuppies = g.levelMaxMegaPuppies;
      if (g.levelUndos > g.mostUndos) g.mostUndos = g.levelUndos;
      if (g.levelBonuses > g.mostBonuses) g.mostBonuses = g.levelBonuses;
      this.persist();
      this.scene.start('Interstitial', {
        levelScore: g.levelScore,
        levelNumber: g.levelNumber,
        levelPuppiesPlayed: g.levelPuppiesPlayed,
        levelMegaPuppies: g.levelMegaPuppies,
        levelMaxMegaPuppies: g.levelMaxMegaPuppies,
        levelUndos: g.levelUndos,
        levelBonuses: g.levelBonuses,
        bestLevelScore: g.bestLevelScore,
        mostPuppiesPlayed: g.mostPuppiesPlayed,
        mostMegaPuppies: g.mostMegaPuppies,
        mostMaxMegaPuppies: g.mostMaxMegaPuppies,
        mostUndos: g.mostUndos,
        mostBonuses: g.mostBonuses,
      });
      return;
    }

    // Food bonus — port of iOS GameScene.swift:683-754. Only spawn when
    // there are no dogs in the doghouse (the doghouse bonus takes priority
    // when dogs are stuck), respect the per-level cap, and roll a 5/200
    // chance per turn (randomInt(200) landing in [29, 33]).
    if (
      this.puppyGame.dogsInDoghouse === 0 &&
      this.puppyGame.levelBonuses < maxFoodBonusForLevel(this.puppyGame.levelNumber)
    ) {
      const roll = Math.floor(Math.random() * 200);
      if (roll >= 29 && roll <= 33) {
        const cell = this.puppyGame.board.spawnBonus(() => Math.random());
        if (cell >= 0) {
          this.puppyGame.levelBonuses += 1;
          this.foodBonus.spawnAt(cell, 15, () => {
            this.puppyGame.board.clearBonus();
            this.boardView.rerender();
          });
        }
      }
    }

    // Weather check after each placement (post-level 3). The sprinkler path
    // was removed and will be reimplemented later — for now only thunderstorm
    // fires, and the sprinkler weather kind is simply ignored. Thunderstorm
    // also skips when there's nothing to regrow (no cleared grass anywhere).
    const weather = shouldTriggerWeather(this.puppyGame.levelNumber, () => Math.random());
    if (weather === 'thunderstorm' && this.hasClearedGrass()) {
      this.fireThunderstorm();
    }

    this.setActive(this.pickActivePuppy());
    this.headerView.refresh(this.active.puppy, this.active.color);
    this.headerView.hideDogHouseSign();
    this.updateHint();
    this.maybeShowGoToDogHouse();
    this.persist();

    if (this.isStuck()) this.openOhNo();
  }

  // Debug overlay: 15px white grid across the whole canvas, used for
  // pixel-precise direction during layout iteration.
  // @ts-expect-error -- kept for ad-hoc re-enable; not called normally.
  private drawDebugGrid(): void {
    const step = 15;
    const w = LOGICAL_WIDTH;
    const h = LOGICAL_HEIGHT;
    const g = this.add.graphics();
    g.setDepth(9999);
    g.lineStyle(1, 0xffffff, 0.5);
    for (let x = 0; x <= w; x += step) {
      g.lineBetween(x, 0, x, h);
    }
    for (let y = 0; y <= h; y += step) {
      g.lineBetween(0, y, w, y);
    }
  }

  // Mirrors iOS: when the active puppy has zero playable squares and the
  // doghouse still has room, show the "Go to the Dog House!" sign. Tapping
  // the doghouse retreats the puppy and rolls a new active.
  private goToDogHouse(): void {
    if (!this.headerView) return;
    if (this.puppyGame.dogsInDoghouse >= 3) return;
    // Only meaningful when the player can't place the current puppy; ignore
    // taps while there are still playable squares.
    const { playableSquares } = getPlayableSquares(
      this.puppyGame.board,
      this.active.puppy,
      this.active.color,
      false,
      false,
    );
    if (playableSquares > 0) return;

    this.puppyGame.dogsInDoghouse += 1;
    this.soundBank.playSfx('woof');
    this.headerView.hideDogHouseSign();
    this.setActive(this.pickActivePuppy());
    this.headerView.refresh(this.active.puppy, this.active.color);
    this.updateHint();
    // Re-evaluate after the reroll — the new puppy may also have no playable
    // squares, in which case the "Go to the Dog House!" sign should stay up.
    this.maybeShowGoToDogHouse();
    this.persist();
    // If retreating just filled the doghouse and the new puppy has nowhere
    // to play and no CrazySquares are banked, the level is unwinnable.
    if (this.isStuck()) this.openOhNo();
  }

  private maybeShowGoToDogHouse(): void {
    const { playableSquares } = getPlayableSquares(
      this.puppyGame.board,
      this.active.puppy,
      this.active.color,
      false,
      false,
    );
    const stuckThisPuppy = playableSquares === 0;
    // "Use a Crazy Square!" — only when the active puppy can't be played AND
    // the player has at least one CrazySquare banked.
    this.headerView.setUseCrazySignVisible(
      stuckThisPuppy && this.puppyGame.crazySquares > 0,
    );
    // "Go to the Dog House!" — only when stuck AND the doghouse still has
    // room. (showDogHouseSign also self-guards on doghouse-full.)
    if (this.puppyGame.dogsInDoghouse >= 3) {
      this.headerView.hideDogHouseSign();
      return;
    }
    if (stuckThisPuppy) {
      // Play the iOS "oh no!" sfx the moment the sign first appears
      // (Header.swift L2013).
      this.headerView.showDogHouseSign(() => this.soundBank.playSfx('ohnooo'));
    } else {
      this.headerView.hideDogHouseSign();
    }
  }

  private updateHint(): void {
    // Best-square paw is a Kid Mode feature (iOS prefSkillLevel == 1).
    if (!this.settings.hints || !this.settings.kidMode) {
      this.boardView.hideHint();
      return;
    }
    const bestIdx = this.bestHintCell();
    if (bestIdx >= 0) {
      this.boardView.showHint(bestIdx);
    } else {
      this.boardView.hideHint();
    }
  }

  private isStuck(): boolean {
    const { playableSquares } = getPlayableSquares(
      this.puppyGame.board,
      this.active.puppy,
      this.active.color,
      false,
      false,
    );
    if (playableSquares > 0) return false;
    if (this.puppyGame.dogsInDoghouse < 3) return false;
    if (this.puppyGame.crazySquares > 0) return false;
    return true;
  }

  // True iff there's at least one regrowable cell (cleared/mown empty grass
  // with no puppy/hydrant/obstacle/mega slot). Used to gate the thunderstorm:
  // a strike with nothing to regrow is just a noisy no-op.
  private hasClearedGrass(): boolean {
    const board = this.puppyGame.board;
    for (let i = 0; i < board.numSquares; i++) {
      const d = board.squareAt(i).data;
      if (
        d.isClear &&
        d.puppy === null &&
        !d.isFireHydrant &&
        !d.underCrazyMega &&
        !board.obstacleAtIndex(i)
      ) {
        return true;
      }
    }
    return false;
  }

  // Shared thunderstorm trigger — used by the real weather roll and the L
  // debug key. Plays sfx, locks input, and regrows 4–6 grass cells at the
  // strike's midpoint (the lightning revives some mown grass).
  private fireThunderstorm(): void {
    this.soundBank.playSfx('thunder');
    this.input.enabled = false;
    playThunderstorm(
      this,
      () => {
        const regrown = this.puppyGame.board.regrowRandomCleared(
          4 + Math.floor(Math.random() * 3),
          () => Math.random(),
        );
        this.boardView.rerender();
        this.boardView.animateRegrow(regrown.map((r) => r.index), 2000);
      },
      () => {
        this.input.enabled = true;
      },
    );
  }

  private openAnimDebug(): void {
    this.input.enabled = false;
    this.scene.launch('AnimDebug', {
      onClose: () => {
        this.input.enabled = true;
      },
    });
  }

  private openOhNo(): void {
    const parkIdx =
      this.puppyGame.parkNames.length === 0
        ? 0
        : (this.puppyGame.levelNumber - 1) % this.puppyGame.parkNames.length;
    const parkName = this.puppyGame.parkNames[parkIdx] ?? '';
    this.soundBank.playSfx('ohnooo');
    this.input.enabled = false;
    this.scene.launch('OhNo', {
      parkName,
      levelNumber: this.puppyGame.levelNumber,
      onReplay: () => this.replayCurrentLevel(),
    });
  }

  private replayCurrentLevel(): void {
    this.input.enabled = true;
    this.puppyGame.levelScore = 0;
    this.puppyGame.levelPuppiesPlayed = 0;
    this.puppyGame.levelBonuses = 0;
    this.puppyGame.levelMegaPuppies = 0;
    this.puppyGame.dogsInDoghouse = 0;
    this.puppyGame.board = new Board(
      this.puppyGame.numSquaresHoriz,
      this.puppyGame.numSquaresVert,
    );
    this.seedFireHydrant();
    this.puppyGame.board.placeObstaclesForLevel(
      this.puppyGame.levelNumber,
      defaultRng(this.puppyGame.levelNumber),
    );
    // Tear down the previous BoardView's container and any leftover mega
    // visuals before instantiating the new one. Mega sprites are attached
    // directly to the scene (not the BoardView container), so they'd
    // otherwise linger after a Replay.
    this.boardView.destroy();
    this.boardView = new BoardView(this, this.puppyGame.board, (cell) => {
      if (this.settingsOverlay) return;
      // A SuperMega tap already consumed this click (conversion). Don't also
      // place the active puppy into the cell it cleared.
      if (this.megaJustTapped) return;
      if (this.scene.isActive('DogTraining') || this.scene.isActive('Welcome')) return;
      this.tryPlace(cell);
    });
    this.setActive(this.pickActivePuppy());
    this.boardView.rerender();
    this.headerView.refresh(this.active.puppy, this.active.color);
    this.persist();
  }

  private onMegaTapped(topLeftIndex: number): void {
    // Block the scene-level board pointerdown that fires right after this
    // gameobjectdown for the same tap — otherwise it would place the active
    // puppy into a cell this conversion just cleared. Cleared next tick.
    this.megaJustTapped = true;
    this.time.delayedCall(0, () => { this.megaJustTapped = false; });

    const horiz = this.puppyGame.numSquaresHoriz;
    const cellIndexes = [
      topLeftIndex,
      topLeftIndex + 1,
      topLeftIndex + horiz,
      topLeftIndex + 1 + horiz,
    ];

    // Clear all 4 underlying cells' data and force their per-cell sprites
    // invisible+empty before the clear paths below run, so no stale puppy
    // can show through the freshly converted CrazySquare.
    for (const idx of cellIndexes) {
      const d = this.puppyGame.board.squareAt(idx).data;
      d.puppy = null;
      d.color = null;
      d.isClear = true;
      d.underCrazyMega = false;
      this.boardView.forceClearCellSprite(idx);
    }

    const clearedIndexes = this.puppyGame.board.convertMegaToCrazy(topLeftIndex);
    this.boardView.destroyMegaVisual(topLeftIndex);
    this.puppyGame.crazySquares += 1;
    this.boardView.rerender();

    for (const idx of clearedIndexes) {
      this.boardView.sparkleAt(idx);
    }
    this.headerView.refresh(this.active.puppy, this.active.color);
    this.soundBank.playSfx('yippee');
    this.persist();
  }

  // Snapshot the most recent placement so the player can tap Undo to roll it
  // back. The button auto-hides after a brief 1.5s window.
  private recordUndo(snap: {
    cellIndex: number;
    scoreDelta: number;
    prevActive: { puppy: PuppyId; color: Color };
  }): void {
    this.lastUndo = snap;
    this.headerView.showUndoButton();
    this.undoHideTimer?.remove();
    this.undoHideTimer = this.time.delayedCall(1500, () => this.clearUndo());
  }

  private clearUndo(): void {
    this.lastUndo = null;
    this.headerView.hideUndoButton();
    this.undoHideTimer?.remove();
    this.undoHideTimer = null;
  }

  private doUndo(): void {
    const u = this.lastUndo;
    if (!u) return;
    const sq = this.puppyGame.board.squareAt(u.cellIndex).data;
    // Restore the cell to its pre-placement state: empty puppy/color and
    // not cleared (it was clean grass before this placement).
    sq.puppy = null;
    sq.color = null;
    sq.isClear = false;
    this.puppyGame.gameScore -= u.scoreDelta;
    this.puppyGame.levelScore -= u.scoreDelta;
    this.puppyGame.gamePuppiesPlayed = Math.max(0, this.puppyGame.gamePuppiesPlayed - 1);
    this.puppyGame.levelPuppiesPlayed = Math.max(0, this.puppyGame.levelPuppiesPlayed - 1);
    this.puppyGame.gameUndos += 1;
    this.puppyGame.levelUndos += 1;
    this.setActive(u.prevActive);
    this.clearUndo();
    this.soundBank.playSfx('click');
    this.boardView.rerender();
    this.headerView.refresh(this.active.puppy, this.active.color);
    this.headerView.hideDogHouseSign();
    this.updateHint();
    this.maybeShowGoToDogHouse();
    this.persist();
  }

  private useCrazySquare(): void {
    if (this.puppyGame.crazySquares <= 0) return;
    this.puppyGame.crazySquares -= 1;
    this.puppyGame.rerollAfterCrazy = true;
    for (let i = 0; i < 50; i++) {
      const next = this.pickActivePuppy();
      if (next.puppy !== this.active.puppy && next.color !== this.active.color) {
        this.setActive(next);
        break;
      }
    }
    this.headerView.refresh(this.active.puppy, this.active.color);
    this.updateHint();
    this.maybeShowGoToDogHouse();
    this.persist();
    // After spending the last CrazySquare, the player may now be fully stuck.
    if (this.isStuck()) this.openOhNo();
  }

  private openDogTraining(): void {
    this.input.enabled = false;
    this.scene.launch('DogTraining', {
      onComplete: () => {
        this.input.enabled = true;
        this.puppyGame.hasFinishedDogTraining = true;
        this.persist();
        // First-launch flow only: now that the board is interactive again,
        // show the onboarding hint that was deferred during this overlay.
        if (this.pendingOnboardingHint) {
          this.pendingOnboardingHint = false;
          this.runTurnStartHint();
        }
      },
    });
  }

  private openWelcome(): void {
    this.input.enabled = false;
    this.scene.launch('Welcome', {
      onComplete: () => {
        this.input.enabled = true;
        this.openDogTraining();
      },
    });
  }

  private openSettings(): void {
    if (this.settingsOverlay) return;
    this.soundBank.playSfx('click');
    this.settingsOverlay = new SettingsOverlay(
      this,
      { ...this.settings },
      (next) => {
        const wasMusicOn = this.settings.music;
        this.settings = next;
        this.soundBank.setMusicEnabled(next.music);
        this.soundBank.setSfxEnabled(next.sfx);
        if (!next.music) {
          this.soundBank.stopMusic();
        } else if (!wasMusicOn) {
          // Music just got toggled on — pick a track and start it.
          const tracks: ('bkgndmusic1' | 'bkgndmusic2' | 'bkgndmusic3' | 'bkgndmusic4')[] = [
            'bkgndmusic1', 'bkgndmusic2', 'bkgndmusic3', 'bkgndmusic4',
          ];
          this.soundBank.startMusic(tracks[this.puppyGame.randomInt(tracks.length)]!);
        }
        // Hints/Kid Mode toggles need to take effect immediately, not wait
        // for the next placement to re-run updateHint.
        this.updateHint();
        this.persist();
      },
      () => {
        // Defer nulling the reference one tick so the scene-level board
        // pointerdown listener (which fires AFTER gameobjectdown) still sees a
        // non-null overlay and skips placement.
        this.settingsOverlay?.destroy();
        this.time.delayedCall(0, () => { this.settingsOverlay = null; });
      },
      () => this.startNewGame(),
      () => {
        // Close the overlay first so the scene swap doesn't trip over a
        // stale settings container.
        this.settingsOverlay?.destroy();
        this.settingsOverlay = null;
        this.replayCurrentLevel();
      },
    );
  }

  // Wipe the saved bundle and re-run the scene from scratch. create() will
  // see no advanceLevel data and load the freshly defaulted bundle, so
  // everything (level, score, board, dogs in house, kid-mode hints, etc.)
  // resets to the same state as a brand-new install.
  private startNewGame(): void {
    this.soundBank.playSfx('yeah');
    this.settingsOverlay?.destroy();
    this.settingsOverlay = null;
    // Reset in-memory settings to their first-launch defaults. The class-field
    // initializer only runs on scene construction, not on scene.restart(), so
    // without this the user's prior toggles (music/sfx/hints/kidMode) would
    // survive the wipe even though localStorage is gone.
    this.settings = { music: true, sfx: true, hints: true, kidMode: false };
    // Discard any pending debounced save first; otherwise flushing it would
    // re-write the old game over the cleared default and the new game wouldn't
    // truly start fresh (hintsPlayDog and board state would survive).
    this.save.cancelPendingSave();
    this.save.clear();
    this.scene.restart();
  }

  private persist(): void {
    const bundle = this.puppyGame.currentBundle(true);
    bundle.settings = { ...this.settings };
    this.save.debouncedSave(bundle);
  }
}
